import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, Lock } from "lucide-react";
import { useI18n } from "../lib/i18n.jsx";
import { setMarketingConsent } from "../lib/marketingPixels.js";

const STORAGE_KEY = "scootive_cookie_consent";

// Default categories. `necessary` is always true and cannot be toggled off.
const DEFAULTS = {
  necessary: true,
  functional: false,
  analytics: false,
  marketing: false,
};

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    // Backward-compat with the previous string-based scheme.
    if (raw === "all") return { necessary: true, functional: true, analytics: true, marketing: true };
    if (raw === "essential") return { ...DEFAULTS };
    const parsed = JSON.parse(raw);
    return { ...DEFAULTS, ...parsed, necessary: true };
  } catch (e) {
    return null;
  }
}

function pushConsent(prefs) {
  // Update Google Analytics consent state via gtag Consent Mode v2.
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      analytics_storage: prefs.analytics ? "granted" : "denied",
      ad_storage: prefs.marketing ? "granted" : "denied",
      ad_user_data: prefs.marketing ? "granted" : "denied",
      ad_personalization: prefs.marketing ? "granted" : "denied",
      functionality_storage: prefs.functional ? "granted" : "denied",
      personalization_storage: prefs.functional ? "granted" : "denied",
    });
  }
  // Lazy-load + arm Meta Pixel only when marketing consent is granted.
  setMarketingConsent(!!prefs.marketing);
}

export function reopenCookieSettings() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    /* ignore */
  }
  window.dispatchEvent(new Event("cookie-consent-reopen"));
}

function Switch({ checked, disabled, onChange, testId, ariaLabel }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={disabled ? undefined : onChange}
      data-testid={testId}
      className={`shrink-0 relative w-11 h-6 rounded-full transition-colors ${
        checked ? "bg-summer" : "bg-winter/20"
      } ${disabled ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span
        className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform flex items-center justify-center ${
          checked ? "translate-x-5" : "translate-x-0.5"
        }`}
      >
        {disabled ? <Lock className="w-2.5 h-2.5 text-winter/50" /> : null}
      </span>
    </button>
  );
}

export default function CookieConsent() {
  const { lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [customize, setCustomize] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULTS);

  useEffect(() => {
    const stored = readStored();
    if (!stored) {
      setOpen(true);
    } else {
      pushConsent(stored);
    }
    const onReopen = () => {
      setCustomize(false);
      setPrefs(readStored() || DEFAULTS);
      setOpen(true);
    };
    window.addEventListener("cookie-consent-reopen", onReopen);
    return () => window.removeEventListener("cookie-consent-reopen", onReopen);
  }, []);

  const persist = (next) => {
    const safe = { ...next, necessary: true };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(safe));
    } catch (e) {
      /* ignore */
    }
    pushConsent(safe);
    setPrefs(safe);
    setOpen(false);
    setCustomize(false);
  };

  const acceptAll = () => persist({ necessary: true, functional: true, analytics: true, marketing: true });
  const essentialOnly = () => persist({ ...DEFAULTS });
  const saveCustom = () => persist(prefs);

  if (!open) return null;

  const COPY = {
    pl: {
      title: "Cookies & prywatność",
      body:
        "Używamy plików cookies, by strona działała poprawnie i (za Twoją zgodą) sprawdzać anonimowo, co rodzice klikają najczęściej. To pomaga nam ulepszać obóz.",
      more: "Polityka prywatności",
      accept: "Akceptuj wszystkie",
      essential: "Tylko niezbędne",
      customize: "Dostosuj",
      customizeTitle: "Ustawienia cookies",
      customizeIntro:
        "Wybierz, na które kategorie cookies wyrażasz zgodę. Decyzję możesz zmienić w każdej chwili.",
      cats: {
        necessary: { title: "Niezbędne", desc: "Wymagane do działania strony (sesja, banner zgody, bezpieczeństwo). Nie wymagają zgody." },
        functional: { title: "Funkcjonalne", desc: "Zapamiętują Twoje preferencje (np. język, ustawienia widoku) — żeby strona była wygodniejsza w użyciu." },
        analytics: { title: "Analityczne", desc: "Anonimowe statystyki Google Analytics 4 (zanonimizowane IP). Pomagają nam ulepszać stronę." },
        marketing: { title: "Marketingowe", desc: "Pozwalają mierzyć skuteczność reklam i pokazywać Ci dopasowane treści." },
      },
      save: "Zapisz wybór",
      cancel: "Anuluj",
    },
    uk: {
      title: "Cookies & приватність",
      body:
        "Ми використовуємо cookies, щоб сайт працював коректно і (за твоєю згодою) анонімно перевіряти, що батьки клікають найчастіше. Це допомагає нам покращувати табір.",
      more: "Політика конфіденційності",
      accept: "Прийняти всі",
      essential: "Тільки необхідні",
      customize: "Налаштувати",
      customizeTitle: "Налаштування cookies",
      customizeIntro:
        "Обери, на які категорії cookies погоджуєшся. Рішення можна змінити в будь-який момент.",
      cats: {
        necessary: { title: "Необхідні", desc: "Потрібні для роботи сайту (сесія, банер згоди, безпека). Не потребують згоди." },
        functional: { title: "Функціональні", desc: "Запам'ятовують твої налаштування (наприклад, мову, вигляд) — щоб сайт був зручнішим." },
        analytics: { title: "Аналітичні", desc: "Анонімна статистика Google Analytics 4 (анонімізована IP). Допомагає нам покращувати сайт." },
        marketing: { title: "Маркетингові", desc: "Дозволяють вимірювати ефективність реклами і показувати тобі релевантний контент." },
      },
      save: "Зберегти вибір",
      cancel: "Скасувати",
    },
    en: {
      title: "Cookies & privacy",
      body:
        "We use cookies so the site works properly and (with your consent) to anonymously see what parents click most often. It helps us improve the camp.",
      more: "Privacy policy",
      accept: "Accept all",
      essential: "Essential only",
      customize: "Customize",
      customizeTitle: "Cookie settings",
      customizeIntro:
        "Choose which cookie categories you consent to. You can change your decision anytime.",
      cats: {
        necessary: { title: "Necessary", desc: "Required for the site to work (session, consent banner, security). No consent needed." },
        functional: { title: "Functional", desc: "Remember your preferences (e.g. language, view settings) for a better experience." },
        analytics: { title: "Analytics", desc: "Anonymous Google Analytics 4 stats (IP anonymised). Helps us improve the site." },
        marketing: { title: "Marketing", desc: "Measure ad performance and show you tailored content." },
      },
      save: "Save choice",
      cancel: "Cancel",
    },
  };
  const T = COPY[lang] || COPY.en;
  const policyHref = lang === "pl" ? "/polityka-prywatnosci" : "/privacy-policy";

  const categoryRows = [
    { key: "necessary", locked: true },
    { key: "functional", locked: false },
    { key: "analytics", locked: false },
    { key: "marketing", locked: false },
  ];

  // Customize modal — full-screen overlay
  if (customize) {
    return (
      <div
        data-testid="cookie-customize-modal"
        className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-winter/40 backdrop-blur-sm"
        role="dialog"
        aria-modal="true"
      >
        <div className="bg-cream w-full max-w-lg rounded-3xl shadow-2xl border border-winter/10 overflow-hidden max-h-[92vh] flex flex-col">
          <div className="flex items-center justify-between px-6 pt-6 pb-3 shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-summer/15 flex items-center justify-center">
                <Cookie className="w-4 h-4 text-summer" />
              </div>
              <h3 className="font-display font-bold text-winter text-lg">
                {T.customizeTitle}
              </h3>
            </div>
            <button
              onClick={() => setCustomize(false)}
              data-testid="cookie-customize-close"
              className="w-9 h-9 rounded-full hover:bg-winter/5 flex items-center justify-center text-winter/60 hover:text-winter transition-colors"
              aria-label={T.cancel}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="px-6 pb-2 shrink-0">
            <p className="text-sm text-winter/70 leading-relaxed">
              {T.customizeIntro}{" "}
              <Link
                to={policyHref}
                onClick={() => setOpen(false)}
                className="text-summer font-semibold hover:underline whitespace-nowrap"
                data-testid="cookie-customize-policy-link"
              >
                {T.more} →
              </Link>
            </p>
          </div>

          <div className="px-6 py-4 space-y-2.5 overflow-y-auto">
            {categoryRows.map(({ key, locked }) => {
              const cat = T.cats[key];
              const checked = locked ? true : !!prefs[key];
              return (
                <div
                  key={key}
                  data-testid={`cookie-cat-${key}`}
                  className={`flex items-center justify-between gap-4 p-4 rounded-2xl border ${
                    locked
                      ? "bg-winter/5 border-winter/10"
                      : "bg-white border-winter/10"
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <h4 className="font-display font-bold text-winter text-sm mb-1">
                      {cat.title}
                    </h4>
                    <p className="text-xs text-winter/65 leading-snug">
                      {cat.desc}
                    </p>
                  </div>
                  <Switch
                    checked={checked}
                    disabled={locked}
                    testId={`cookie-toggle-${key}`}
                    ariaLabel={cat.title}
                    onChange={() => setPrefs((p) => ({ ...p, [key]: !p[key] }))}
                  />
                </div>
              );
            })}
          </div>

          <div className="px-6 pb-6 pt-2 flex flex-col-reverse sm:flex-row gap-2 shrink-0 border-t border-winter/5">
            <button
              onClick={() => setCustomize(false)}
              data-testid="cookie-customize-cancel"
              className="flex-1 px-4 py-2.5 rounded-full border-2 border-winter/15 hover:border-winter/30 text-winter font-semibold text-sm transition-colors"
            >
              {T.cancel}
            </button>
            <button
              onClick={saveCustom}
              data-testid="cookie-customize-save"
              className="flex-1 btn-pill btn-pill-primary text-sm py-2.5 px-5 justify-center"
            >
              {T.save}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="cookie-banner"
      className="fixed inset-x-0 bottom-0 md:bottom-4 md:inset-x-4 lg:left-auto lg:right-6 lg:max-w-md z-50 mx-auto"
    >
      <div className="m-3 md:m-0 bg-cream border border-winter/15 shadow-2xl rounded-3xl p-5 md:p-6">
        <div className="flex items-start gap-3 mb-3">
          <div className="shrink-0 w-9 h-9 rounded-full bg-summer/15 flex items-center justify-center">
            <Cookie className="w-4 h-4 text-summer" />
          </div>
          <div>
            <h3 className="font-display font-bold text-winter text-base mb-1">
              {T.title}
            </h3>
            <p className="text-sm text-winter/70 leading-relaxed">
              {T.body}{" "}
              <Link
                to={policyHref}
                onClick={() => setOpen(false)}
                className="text-summer font-semibold hover:underline whitespace-nowrap"
                data-testid="cookie-policy-link"
              >
                {T.more} →
              </Link>
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 mt-4">
          <button
            onClick={essentialOnly}
            data-testid="cookie-essential"
            className="flex-1 px-4 py-2.5 rounded-full border-2 border-winter/15 hover:border-winter/30 text-winter font-semibold text-sm transition-colors"
          >
            {T.essential}
          </button>
          <button
            onClick={() => {
              setPrefs(readStored() || DEFAULTS);
              setCustomize(true);
            }}
            data-testid="cookie-customize"
            className="flex-1 px-4 py-2.5 rounded-full border-2 border-winter/15 hover:border-winter/30 text-winter font-semibold text-sm transition-colors"
          >
            {T.customize}
          </button>
          <button
            onClick={acceptAll}
            data-testid="cookie-accept-all"
            className="flex-1 btn-pill btn-pill-primary text-sm py-2.5 px-5 justify-center"
          >
            {T.accept}
          </button>
        </div>
      </div>
    </div>
  );
}
